// Family Principal portal sections — small, focused, all read-from-props.
// Each section honours portal-user.visibility flags (the parent decides
// which to render). Designed for the executive, low-noise experience.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  TrendingUp, TrendingDown, Users, Briefcase, Mail, Phone, FileText, Download,
  Inbox, FilePlus2, Activity, Building2, MessageCircle, Sparkles, Clock,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ResponsiveContainer, LineChart, Line, Tooltip, YAxis } from 'recharts';
import { cn } from '@/lib/utils';

// ──────────────────────────────────────────────────────────────────────────
// Shared
// ──────────────────────────────────────────────────────────────────────────

export function SectionShell({ title, subtitle, icon: Icon, action, children, testId }) {
  return (
    <section className="bg-white rounded-2xl border border-[#E6E0D9] p-6 sm:p-7" data-testid={testId}>
      <header className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="w-9 h-9 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-[#4A3B32]" />
            </div>
          )}
          <div>
            <h2 className="font-heading text-lg text-[#2C2420] leading-tight">{title}</h2>
            {subtitle && <p className="text-sm text-[#8C8078] mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function fmtMoneyCompact(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

// ──────────────────────────────────────────────────────────────────────────
// Welcome / relationship lead note
// ──────────────────────────────────────────────────────────────────────────

export function WelcomeSection({ portalUser, clientName, leadAdviser }) {
  const firstName = (portalUser?.name || '').split(' ')[0] || 'there';
  const customNote = (portalUser?.customWelcomeNote || '').trim();
  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-[#2C2420] via-[#3A2E26] to-[#4A3B32] text-white p-7 sm:p-9 relative overflow-hidden"
      data-testid="portal-welcome-section"
    >
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: '24px 24px' }} />
      <div className="relative">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">{clientName}</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-white mb-3">Welcome back, {firstName}</h1>
        <p className="text-white/80 max-w-2xl leading-relaxed">
          {customNote || `Your private portal — wealth at a glance, what's open, and a quiet pulse from your team.`}
        </p>
        {leadAdviser && (
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-white/70">
            <Briefcase className="w-3.5 h-3.5" />
            Your relationship lead: <span className="text-white font-medium">{leadAdviser.name}</span>
          </div>
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Wealth Snapshot
// ──────────────────────────────────────────────────────────────────────────

const CLASS_COLOURS = {
  'Australian Equities': '#4A3B32',
  'International Equities': '#A86A3A',
  'Fixed Income': '#7D8C9E',
  'Property': '#6B8E6B',
  'Alternatives': '#B89564',
  'Private Markets': '#5D524D',
  'Cash': '#D4C4A8',
  'Other': '#8C8078',
};

export function WealthSnapshotSection({ data }) {
  const { totalValue, unrealisedGain, unrealisedPct, ytdReturn, allocation = [], sparkline = [] } = data || {};
  const ytdPositive = (ytdReturn ?? 0) >= 0;

  if (!totalValue || totalValue === 0) {
    return (
      <SectionShell title="Wealth Snapshot" icon={TrendingUp} testId="portal-wealth-section">
        <p className="text-sm text-[#8C8078] py-6 text-center">Your wealth snapshot will appear here once your portfolio is set up.</p>
      </SectionShell>
    );
  }

  return (
    <SectionShell title="Wealth Snapshot" subtitle="A live view of your family wealth" icon={TrendingUp} testId="portal-wealth-section">
      {/* Hero row */}
      <div className="grid sm:grid-cols-[1.4fr_1fr] gap-6 items-end">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-1">Total wealth</p>
          <p className="font-heading text-5xl text-[#2C2420] tabular-nums" data-testid="portal-wealth-total">
            {fmtMoneyCompact(totalValue)}
          </p>
          {ytdReturn !== null && ytdReturn !== undefined && (
            <p className={cn('mt-2 inline-flex items-center gap-1 text-sm', ytdPositive ? 'text-green-700' : 'text-red-600')}>
              {ytdPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {ytdPositive ? '+' : ''}{ytdReturn.toFixed(1)}% year-to-date
            </p>
          )}
        </div>
        <div className="h-20" data-testid="portal-wealth-sparkline">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip formatter={(v) => fmtMoneyCompact(v)} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
              <Line type="monotone" dataKey="totalValue" stroke={ytdPositive ? '#6B8E6B' : '#B14444'} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sub-metrics */}
      {(unrealisedGain || unrealisedPct !== null) && (
        <div className="mt-5 grid grid-cols-3 gap-3 pt-4 border-t border-[#F0EAE3]">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8C8078]">Unrealised gain</p>
            <p className={cn('text-lg font-heading mt-0.5 tabular-nums', (unrealisedGain || 0) >= 0 ? 'text-green-700' : 'text-red-600')}>
              {fmtMoneyCompact(unrealisedGain)}
            </p>
          </div>
          {unrealisedPct !== null && unrealisedPct !== undefined && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#8C8078]">% of cost</p>
              <p className="text-lg font-heading text-[#2C2420] mt-0.5 tabular-nums">
                {unrealisedPct >= 0 ? '+' : ''}{unrealisedPct.toFixed(1)}%
              </p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#8C8078]">Asset classes</p>
            <p className="text-lg font-heading text-[#2C2420] mt-0.5 tabular-nums">{allocation.length}</p>
          </div>
        </div>
      )}

      {/* Allocation breakdown */}
      {allocation.length > 0 && (
        <div className="mt-6">
          <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-3">Allocation</p>
          <ul className="space-y-2" data-testid="portal-wealth-allocation">
            {allocation.slice(0, 7).map((a) => (
              <li key={a.assetClass}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#2C2420]">{a.assetClass}</span>
                  <span className="text-sm text-[#5D524D] tabular-nums">
                    {fmtMoneyCompact(a.value)} <span className="text-[#8C8078]">· {a.weight.toFixed(1)}%</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F0EAE3] overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(a.weight, 100)}%`, background: CLASS_COLOURS[a.assetClass] || '#5D524D' }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Items requiring your input
// ──────────────────────────────────────────────────────────────────────────

export function ActionItemsSection({ items = [] }) {
  const openItems = items.filter((i) => i.status !== 'completed');
  return (
    <SectionShell
      title="Items requiring your input"
      subtitle={openItems.length > 0 ? `${openItems.length} open` : "You're all caught up"}
      icon={Inbox}
      testId="portal-action-items-section"
    >
      {openItems.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-6 text-center">No outstanding items from your team.</p>
      ) : (
        <ul className="divide-y divide-[#F0EAE3]" data-testid="portal-action-items-list">
          {openItems.slice(0, 10).map((i) => (
            <li key={i.id} id={`portal-item-${i.id}`} data-portal-item-id={i.id} className="py-3 flex items-start gap-3 scroll-mt-24 -mx-2 px-2 rounded-md transition-colors">
              <div className="w-2 h-2 rounded-full bg-[#D99A6C] mt-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2C2420] font-medium">{i.title}</p>
                {i.description && <p className="text-sm text-[#5D524D] mt-0.5 line-clamp-2">{i.description}</p>}
                <p className="text-xs text-[#8C8078] mt-1">
                  {i.dueDate ? `Due ${format(parseISO(i.dueDate), 'd MMM yyyy')}` : 'No due date'}
                  {i.requestedByName && ` · from ${i.requestedByName}`}
                </p>
              </div>
              <Badge variant="outline" className="text-[10px]">{i.priority || 'Open'}</Badge>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Document requests
// ──────────────────────────────────────────────────────────────────────────

export function DocumentRequestsSection({ items = [] }) {
  const openReqs = items.filter((i) => i.status !== 'fulfilled');
  return (
    <SectionShell
      title="Document requests"
      subtitle={openReqs.length > 0 ? `${openReqs.length} document${openReqs.length === 1 ? '' : 's'} needed` : 'Nothing requested'}
      icon={FilePlus2}
      testId="portal-doc-requests-section"
    >
      {openReqs.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-6 text-center">Your team hasn't asked for anything right now.</p>
      ) : (
        <ul className="divide-y divide-[#F0EAE3]" data-testid="portal-doc-requests-list">
          {openReqs.map((r) => (
            <li key={r.id} id={`portal-item-${r.id}`} data-portal-item-id={r.id} className="py-3 scroll-mt-24 -mx-2 px-2 rounded-md transition-colors">
              <p className="text-sm text-[#2C2420] font-medium">{r.title}</p>
              {r.description && <p className="text-sm text-[#5D524D] mt-0.5">{r.description}</p>}
              <p className="text-xs text-[#8C8078] mt-1">
                {r.dueDate ? `Due ${format(parseISO(r.dueDate), 'd MMM yyyy')}` : 'No deadline'}
                {r.requestedByName && ` · requested by ${r.requestedByName}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Documents shared with you
// ──────────────────────────────────────────────────────────────────────────

export function SharedDocumentsSection({ items = [] }) {
  return (
    <SectionShell
      title="Documents shared with you"
      subtitle={items.length > 0 ? `${items.length} recently shared` : 'Nothing shared yet'}
      icon={FileText}
      testId="portal-shared-docs-section"
    >
      {items.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-6 text-center">When your team shares a document with you, it'll appear here.</p>
      ) : (
        <ul className="divide-y divide-[#F0EAE3]" data-testid="portal-shared-docs-list">
          {items.slice(0, 10).map((d) => (
            <li key={d.id} className="py-3 flex items-start gap-3">
              <FileText className="w-4 h-4 text-[#A86A3A] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2C2420] font-medium truncate">{d.name}</p>
                <p className="text-xs text-[#8C8078]">
                  {d.category && <>{d.category} · </>}
                  {d.sharedAt && <>Shared {format(parseISO(d.sharedAt), "d MMM yyyy")}</>}
                  {d.sharedBy && <> · by {d.sharedBy}</>}
                </p>
                {d.note && <p className="text-xs text-[#5D524D] mt-1 italic">"{d.note}"</p>}
              </div>
              <button className="text-[#A86A3A] hover:text-[#8B5530]" title="Open" data-testid={`portal-doc-open-${d.id}`}>
                <Download className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Your Team
// ──────────────────────────────────────────────────────────────────────────

export function YourTeamSection({ members = [] }) {
  if (!members.length) {
    return (
      <SectionShell title="Your Team" icon={Users} testId="portal-team-section">
        <p className="text-sm text-[#8C8078] py-6 text-center">Your team will appear here.</p>
      </SectionShell>
    );
  }
  return (
    <SectionShell title="Your Team" subtitle="The people looking after your family" icon={Users} testId="portal-team-section">
      <ul className="space-y-3" data-testid="portal-team-list">
        {members.map((m) => (
          <li key={m.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#FCFAF7] border border-[#F0EAE3]">
            <div className="w-10 h-10 rounded-full bg-[#4A3B32] text-white flex items-center justify-center text-sm font-medium shrink-0">
              {(m.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm text-[#2C2420] font-medium truncate">{m.name}</p>
                {m.isLead && <Badge className="text-[10px] bg-[#4A3B32] hover:bg-[#4A3B32] text-white">Lead</Badge>}
                {m.kind === 'external' && <Badge variant="outline" className="text-[10px]">External</Badge>}
              </div>
              <p className="text-xs text-[#8C8078] truncate">
                {m.title || m.role || (m.kind === 'external' ? 'External adviser' : 'Adviser')}
                {m.firmName && ` · ${m.firmName}`}
              </p>
              <div className="flex items-center gap-3 mt-2">
                {m.email && (
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 text-xs text-[#A86A3A] hover:text-[#8B5530]" data-testid={`portal-team-email-${m.id}`}>
                    <Mail className="w-3 h-3" /> Email
                  </a>
                )}
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1 text-xs text-[#A86A3A] hover:text-[#8B5530]" data-testid={`portal-team-phone-${m.id}`}>
                    <Phone className="w-3 h-3" /> Call
                  </a>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// What's changed
// ──────────────────────────────────────────────────────────────────────────

const ACTION_LABEL = {
  created: 'created',
  updated: 'updated',
  completed: 'completed',
  shared: 'shared',
  uploaded: 'uploaded',
  scheduled: 'scheduled',
  added: 'added',
};

export function WhatsChangedSection({ items = [] }) {
  if (!items.length) {
    return (
      <SectionShell title="What's changed" icon={Sparkles} testId="portal-whats-changed-section">
        <p className="text-sm text-[#8C8078] py-6 text-center">A timeline of changes will appear here.</p>
      </SectionShell>
    );
  }
  return (
    <SectionShell title="What's changed" subtitle="A quiet pulse of your team's work for you" icon={Sparkles} testId="portal-whats-changed-section">
      <ol className="space-y-3 relative" data-testid="portal-whats-changed-list">
        {items.slice(0, 8).map((item) => (
          <li key={item.id} className="flex items-start gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-[#A86A3A] mt-2 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#2C2420]">
                <span className="font-medium">{item.actor || 'Your team'}</span>{' '}
                <span className="text-[#8C8078]">{ACTION_LABEL[item.action] || item.action}</span>{' '}
                <span className="text-[#4A3B32] font-medium">{item.object}</span>
              </p>
              <p className="text-xs text-[#8C8078]">
                {item.timestamp && format(parseISO(item.timestamp), "d MMM yyyy 'at' h:mm a")}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Family / entity summary
// ──────────────────────────────────────────────────────────────────────────

export function FamilyEntitySection({ individuals = [], entities = [] }) {
  return (
    <SectionShell title="Family & entities" subtitle="A read-only summary" icon={Building2} testId="portal-family-entity-section">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-2">Family members</p>
          {individuals.length === 0 ? (
            <p className="text-sm text-[#8C8078]">None recorded yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="portal-family-individuals">
              {individuals.slice(0, 8).map((i) => (
                <li key={i.id} className="text-sm text-[#2C2420] flex items-baseline justify-between gap-2">
                  <span className="truncate">{i.firstName} {i.lastName}</span>
                  <span className="text-xs text-[#8C8078] shrink-0">{i.familyRole || i.role || ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-2">Entities</p>
          {entities.length === 0 ? (
            <p className="text-sm text-[#8C8078]">None recorded yet.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="portal-family-entities">
              {entities.slice(0, 8).map((e) => (
                <li key={e.id} className="text-sm text-[#2C2420] flex items-baseline justify-between gap-2">
                  <span className="truncate">{e.name}</span>
                  <span className="text-xs text-[#8C8078] shrink-0">{e.type || ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Messages
// ──────────────────────────────────────────────────────────────────────────

export function MessagesSection({ items = [], canPost = true, onPost, posting = false, currentUserId }) {
  const sorted = useMemo(() => [...items].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '')), [items]);
  return (
    <SectionShell title="Messages" subtitle="Direct line to your team" icon={MessageCircle} testId="portal-messages-section">
      {sorted.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-4 text-center">No messages yet — start the conversation below.</p>
      ) : (
        <ul className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="portal-messages-list">
          {sorted.map((m) => {
            const mine = m.authorType === 'family_principal' || m.authorId === currentUserId;
            return (
              <li key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                <div className={cn('max-w-[75%] rounded-2xl px-3.5 py-2', mine ? 'bg-[#4A3B32] text-white' : 'bg-[#FCFAF7] border border-[#F0EAE3] text-[#2C2420]')}>
                  <p className="text-xs opacity-70 mb-0.5">{m.authorName}</p>
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                  <p className="text-[10px] opacity-60 mt-1">{m.createdAt && format(parseISO(m.createdAt), "d MMM, h:mm a")}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {canPost && (
        <MessageComposer onPost={onPost} posting={posting} />
      )}
    </SectionShell>
  );
}

function MessageComposer({ onPost, posting }) {
  const [text, setText] = useState('');

  const submit = () => {
    if (!text.trim()) return;
    onPost?.(text.trim());
    setText('');
  };
  return (
    <div className="mt-4 pt-4 border-t border-[#F0EAE3] flex items-end gap-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Write a message to your team…"
        className="flex-1 rounded-lg border border-[#E6E0D9] px-3 py-2 text-sm text-[#2C2420] focus:outline-none focus:border-[#4A3B32]"
        data-testid="portal-messages-input"
      />
      <button
        onClick={submit}
        disabled={posting || !text.trim()}
        className="px-3 py-2 rounded-lg bg-[#4A3B32] text-white text-sm hover:bg-[#3A2E26] disabled:opacity-50"
        data-testid="portal-messages-send"
      >
        Send
      </button>
    </div>
  );
}
